import * as Sentry from '@sentry/react'
import { onAuthStateChanged, User } from 'firebase/auth'
import posthog from 'posthog-js'

import { auth } from '../firebase'
import { getSessionId } from './session'

const APP_VERSION = import.meta.env.VITE_APP_VERSION

export function trackAnalyticsEvent(event: string, data: any | undefined = undefined) {
  posthog.capture(event, data)
}

/**
 * Properties every `editor_*` event carries. The TripBook editor spans a dozen
 * components (left menu, mobile action sheets, gallery panel, in-page image
 * editor…), most of them several levels deep and some driven from injected DOM
 * rather than React props — so the shared context lives here as module state
 * instead of being threaded through as props. Only one editor is ever open at a
 * time, so a single slot is enough.
 *
 * Registered by `TripBookEditorPage` (see `setEditorAnalyticsContext`) and
 * cleared on unmount, so events fired from other pages never inherit it.
 */
export type EditorAnalyticsContext = {
  trip_id?: string
  trip_book_id?: string
  /** 'desktop' | 'mobilePortrait' | 'mobileLandscape' — the whole point of the editor funnel. */
  surface?: string
  structured_by?: string
  book_type?: string
  pages?: number
  has_agency_code?: boolean
  is_embedded?: boolean
}

let editorAnalyticsContext: EditorAnalyticsContext = {}

export function setEditorAnalyticsContext(context: EditorAnalyticsContext) {
  editorAnalyticsContext = context
}

export function clearEditorAnalyticsContext() {
  editorAnalyticsContext = {}
}

/**
 * Emit a TripBook-editor event, merged with the current editor context.
 * Explicit `data` wins over the context so a caller can override e.g. `surface`.
 */
export function trackEditorEvent(event: string, data?: Record<string, unknown>) {
  trackAnalyticsEvent(event, { ...editorAnalyticsContext, ...(data ?? {}) })
}

export function getTripType(
  startDate?: number,
  endDate?: number
): 'past' | 'present' | 'future' | 'unknown' {
  if (!startDate || !endDate) return 'unknown'
  const now = Date.now()
  if (endDate < now) return 'past'
  if (startDate <= now && endDate >= now) return 'present'
  return 'future'
}

function applyPostHogIdentity(user: User | null) {
  if (user) {
    posthog.identify(user.uid, {
      email: user.email,
      name: user.displayName,
      provider: user.providerData[0]?.providerId ?? 'custom'
    })
    posthog.register({
      session_id: getSessionId(),
      release: `51trips-web@${APP_VERSION}`
    })
  } else if (posthog._isIdentified?.()) {
    // Only reset when leaving an identified session, so anonymous visitors
    // keep their distinct_id across page loads
    posthog.reset()
  }
}

export function initUserIdentification() {
  onAuthStateChanged(auth, (user) => {
    Sentry.setUser(user ? { id: user.uid } : null)
    applyPostHogIdentity(user)
  })
}

export const AnalyticsEvents = {
  CREATE_TRIPBOOK: 'StartTripBook',
  CREATE_TRIPBOOK_INVITATION: 'ClickTripBookInvitation',
  TRIPBOOK_CREATION_PREFS_OPENED: 'OpenTripBookCreationPreferences',
  TRIPBOOK_CREATE_CLICKED: 'ClickCreateTripBook',
  ADD_IMAGES_FROM_MOBILE_CLICKED: 'ClickAddImagesFromMobile',
  ADD_IMAGES_FROM_DESKTOP_CLICKED: 'ClickAddImagesFromDesktop',
  ADD_MORE_IMAGES_CLICKED: 'ClickAddMoreImages',
  SETUP_COMPLETED_GO_TO_TRIPBOOK_CLICKED: 'ClickGoToTripBookAfterSetup',
  CREATE_NEW_TRIPBOOK_CLICKED: 'ClickCreateNewTripBook',
  ADD_AGENCY_CODE_CLICKED: 'ClickAddAgencyCode',
  AGENCY_DIALOG_CLOSED: 'CloseAgencyDialog',
  AGENCY_CODE_SET: 'SetAgencyCode',
  CREATE_TRIPBOOK_FROM_AGENCY_CODE: 'CreateTripBookFromAgencyCode',
  AGENCY_CODE_TRIP_SELECTED: 'SelectAgencyCodeTrip',
  ADD_MORE_IMAGES_TO_TRIPBOOK_CLICKED: 'ClickAddMoreImagesToTripBook',
  TRIP_PREVIEW_CLICKED: 'ClickTripPreview',
  NAVIGATE_TO_CHECKOUT: 'NavigateToCheckout',
  EDIT_TRIP_DETAILS_CLICKED: 'ClickEditTripDetails',
  EDIT_FEATURED_LOCATION_CLICKED: 'ClickEditFeaturedLocation',
  EDIT_IMAGES_OF_TRIPDAY_CLICKED: 'ClickEditImagesOfTripDay',
  NAVIGATE_TO_NEXT_DAY_CLICKED: 'ClickNavigateToNextDay',
  NAVIGATE_TO_PREV_DAY_CLICKED: 'ClickNavigateToPreviousDay',
  RATING_CHANGED: 'ChangeTripDayRating',
  BEST_MOMENT_CHANGED: 'ChangeTripDayBestMoment',
  DESCRIPTION_CHANGED: 'ChangeTripDayDescription',
  TITLE_CHANGED: 'ChangeTripDayTitle',
  EDIT_DESCRIPTION_CLICKED: 'ClickEditTripDayDescription',
  SHARE_TRIPBOOK_PREVIEW: 'ShareTripBookPreview',
  CHANGE_COLOR: 'ChangeTripBookColor',
  CHANGE_LANGUAGE: 'ChangeTripBookLanguage',
  EDIT_TRIP: 'EditTrip',
  CONTINUE_TO_INFORMATION: 'ContinueToInformationCheckout',
  CONTINUE_TO_SUMMARY_CHECKOUT: 'ContinueToSummaryCheckout',
  DELETE_TRIP_COMPANION: 'DeleteTripCompanion',
  EDIT_TRIP_COMPANION: 'EditTripCompanion',
  SAVE_TRIP_COMPANION: 'SaveTripCompanion',
  ADD_TRIP_COMPANION: 'AddTripCompanion',
  START_NEW_TRIPBOOK_WITH_INVITATION: 'StartNewTripBookWithInvitation',
  START_NEW_TRIPBOOK_WITH_INVALID_INVITATION: 'StartNewTripBookWithInvalidInvitation',
  CLOSE_TRIPBOOK_PREFERENCE_DIALOG: 'CloseTripBookPreferenceDialog',
  CONTINUE_TRIPBOOK_PREFERENCE_DIALOG: 'ContinueTripBookPreferenceDialog',
  CLICK_CONTACT_US: 'ClickContactUs',
  START_SIGNIN_SIGNUP: 'StartSignInSignUp',
  CLICK_ADD_IMAGES_FROM_MOBILE: 'ClickAddImagesFromMobile',
  CLICK_ADD_MORE_IMAGES: 'ClickAddMoreImages',
  CLICK_CREATE_TRIPBOOK: 'ClickCreateTripBook',
  CLICK_ADD_IMAGES_FROM_DESKTOP: 'ClickAddImagesFromDesktop',
  CHANGE_COLOR_TRIPBOOK_PREFERENCE: 'ChangeTripBookPreferenceColor',
  CHANGE_LANGUAGE_TRIPBOOK_PREFERENCE: 'ChangeTripBookPreferenceLanguage',
  VISIT_INVITATION_LP: 'VisitInvitationLandingPage',
  VISIT_INVITATION_LP_WITH_INVALID_CODE: 'VisitInvitationLandingPageWithInvalidCode',
  EDIT_TRIP_DATES: 'EditTripDates',
  SAVE_TRIP_DATES: 'SaveTripDates',
  WHATSAPP_CONTACT_CLICKED: 'ClickWhatsAppContact',
  INVITATION_FUNNEL_SET_TRIP_DETAILS_COMPLETED: 'Invitation Funnel Set Trip Details Completed',
  INVITATION_FUNNEL_REGISTRATION_COMPLETED: 'Invitation Funnel Registration Completed',
  INVITATION_FUNNEL_SELECT_PLANNED_NOT_PLANNED_TRIP_COMPLETED:
    'Invitation Funnel Select Planned Not Planned Trip Completed',
  INVITATION_FUNNEL_COMPLETED: 'Invitation Funnel Completed',
  GO_TO_MY_TRIPBOOK_WITH_INVALID_INVITATION: 'Go to my Trip from invitation already used',
  INVITATION_FUNNEL_LOGIN_COMPLETED: 'Invitation Funnel Login Completed',
  INVITATION_FUNNEL_SET_CONTACT_FORM_COMPLETED: 'Invitation Funnel Set Contact Form Completed',
  INVITATION_FUNNEL_SET_SHIPPING_DETAILS_COMPLETED:
    'Invitation Funnel Set Shipping Details Completed',
  INVITATION_FUNNEL_SET_TRIP_ITINERARY_COMPLETED: 'Invitation Funnel Set Trip Itinerary Completed',
  INVITATION_FUNNEL_EXPERIENCE_SELECTED: 'Invitation Funnel Experience Selected',
  INVITATION_FUNNEL_UPLOAD_PHOTOS_FINISHED_CLICKED:
    'Invitation Funnel Upload Photos Finished Clicked',
  REGISTRATION_COMPLETED: 'Registration Completed',
  ORGANISE_TRIP_DAYS: 'Organise Trip Days',
  POSTER_CREATOR_OPENED: 'PosterCreatorOpened',
  POSTER_CREATOR_STEP_CHANGED: 'PosterCreatorStepChanged',
  POSTER_DESIGN_CHANGED: 'PosterDesignChanged',
  POSTER_PHOTO_SELECTED: 'PosterPhotoSelected',
  POSTER_PAINTING_SELECTED: 'PosterPaintingSelected',
  POSTER_COLOR_SELECTED: 'PosterColorSelected',
  POSTER_TITLE_UPDATED: 'PosterTitleUpdated',
  POSTER_SUBTITLE_UPDATED: 'PosterSubtitleUpdated',
  POSTER_EXTRAFIELD_UPDATED: 'PosterExtraFieldUpdated',
  POSTER_INVERT_COLORS: 'PosterInvertColors',
  POSTER_IMAGE_UPLOADED: 'PosterImageUploaded',
  POSTER_DESTINATION_SELECTED: 'PosterDestinationSelected',
  POSTER_SIZE_SELECTED: 'PosterSizeSelected',
  POSTER_FONT_CHANGED: 'PosterFontChanged',
  POSTER_FRAME_SELECTED: 'PosterFrameSelected',
  POSTER_ADDED_TO_CART: 'PosterAddedToCart',
  POSTER_EDITED_FROM_CART: 'PosterEditedFromCart',
  POSTER_REMOVED_FROM_CART: 'PosterRemovedFromCart',
  POSTER_UPDATED_QUANTITY_FROM_CART: 'PosterUpdatedQuantityFromCart',
  POSTER_CHECKOUT_STARTED: 'PosterCheckoutStarted',
  POSTER_CHECKOUT_INFORMATION_COMPLETED: 'PosterCheckoutInformationCompleted',
  POSTER_CHECKOUT_PAYMENT_STARTED: 'PosterCheckoutPaymentStarted',
  POSTER_CHECKOUT_PAYMENT_COMPLETED: 'PosterCheckoutPaymentCompleted',
  POSTER_CHECKOUT_PAYMENT_FAILED: 'PosterCheckoutPaymentFailed',
  POSTER_WHATSAPP_CONTACT_CLICKED: 'PosterWhatsAppContactClicked',
  POSTER_PREVIEW_CLICKED: 'PosterPreviewClicked',
  POSTER_NO_RESULTS_CLICKED: 'PosterNoResultsClicked',
  POSTER_TRUSTPILOT_BANNER_CLICKED: 'PosterTrustpilotBannerClicked',
  OPEN_FAQS_CLICKED: 'OpenFAQsClicked',
  TRIP_BOOK_MODIFICATION: 'TripBookModification',
  POSTER_TRIP_PHOTO_SELECTED: 'PosterTripPhotoSelected',

  // B2C Funnel — onboarding_trip
  ONBOARDING_TRIP_VIEWED: 'onboarding_trip_viewed',
  ONBOARDING_TRIP_DESTINATION_OPENED: 'onboarding_trip_destination_opened',
  ONBOARDING_TRIP_DESTINATION_SEARCHED: 'onboarding_trip_destination_searched',
  ONBOARDING_TRIP_DESTINATION_SELECTED: 'onboarding_trip_destination_selected',
  ONBOARDING_TRIP_DESTINATION_REMOVED: 'onboarding_trip_destination_removed',
  ONBOARDING_TRIP_DATES_START_SET: 'onboarding_trip_dates_start_set',
  ONBOARDING_TRIP_DATES_END_SET: 'onboarding_trip_dates_end_set',
  ONBOARDING_TRIP_NO_DATES_CHECKED: 'onboarding_trip_no_dates_checked',
  ONBOARDING_TRIP_CONTINUED: 'onboarding_trip_continued',

  // B2C Funnel — onboarding_traveller
  ONBOARDING_TRAVELLER_VIEWED: 'onboarding_traveller_viewed',
  ONBOARDING_TRAVELLER_SIGNUP_STARTED: 'onboarding_traveller_signup_started',
  ONBOARDING_TRAVELLER_SIGNUP_COMPLETED: 'onboarding_traveller_signup_completed',
  ONBOARDING_TRAVELLER_GOOGLE_CLICKED: 'onboarding_traveller_google_clicked',
  ONBOARDING_TRAVELLER_APPLE_CLICKED: 'onboarding_traveller_apple_clicked',
  ONBOARDING_TRAVELLER_LOGIN_COMPLETED: 'onboarding_traveller_login_completed',
  ONBOARDING_TRAVELLER_WHATSAPP_VIEWED: 'onboarding_traveller_whatsapp_viewed',
  ONBOARDING_TRAVELLER_WHATSAPP_SUBMITTED: 'onboarding_traveller_whatsapp_submitted',
  ONBOARDING_TRAVELLER_WHATSAPP_SKIPPED: 'onboarding_traveller_whatsapp_skipped',

  // B2C Funnel — onboarding_photos
  ONBOARDING_PHOTOS_VIEWED: 'onboarding_photos_viewed',
  ONBOARDING_PHOTOS_UPLOAD_STARTED: 'onboarding_photos_upload_started',
  ONBOARDING_PHOTOS_UPLOAD_COMPLETED: 'onboarding_photos_upload_completed',
  ONBOARDING_PHOTOS_UPLOAD_FAILED: 'onboarding_photos_upload_failed',
  ONBOARDING_PHOTOS_UPLOAD_ERROR: 'onboarding_photos_upload_error',
  ONBOARDING_PHOTOS_UPLOAD_RETRIED: 'onboarding_photos_upload_retried',
  ONBOARDING_PHOTOS_COVER_SET: 'onboarding_photos_cover_set',
  ONBOARDING_PHOTOS_COVER_CHANGED: 'onboarding_photos_cover_changed',
  ONBOARDING_PHOTOS_FAVORITE_ADDED: 'onboarding_photos_favorite_added',
  ONBOARDING_PHOTOS_FAVORITE_REMOVED: 'onboarding_photos_favorite_removed',
  ONBOARDING_PHOTOS_FINALIZE_CLICKED_NO_COVER: 'onboarding_photos_finalize_clicked_no_cover',
  ONBOARDING_PHOTOS_COVER_MODAL_VIEWED: 'onboarding_photos_cover_modal_viewed',
  ONBOARDING_PHOTOS_COVER_MODAL_DISMISSED: 'onboarding_photos_cover_modal_dismissed',
  ONBOARDING_PHOTOS_FINALIZED: 'onboarding_photos_finalized',
  ONBOARDING_PHOTOS_FINALIZED_WITH_FAILED: 'onboarding_photos_finalized_with_failed',

  // B2C Funnel — loading_creating
  LOADING_CREATING_VIEWED: 'loading_creating_viewed',
  LOADING_CREATING_COMPLETED_WITH_METADATA: 'loading_creating_completed_with_metadata',
  LOADING_CREATING_COMPLETED_NO_METADATA: 'loading_creating_completed_no_metadata',
  LOADING_CREATING_FAILED: 'loading_creating_failed',

  // B2C Funnel — waiting_no_metadata
  WAITING_NO_METADATA_VIEWED: 'waiting_no_metadata_viewed',
  WAITING_NO_METADATA_CLOSED: 'waiting_no_metadata_closed',
  WAITING_NO_METADATA_TB_READY: 'waiting_no_metadata_tb_ready',

  // B2C Funnel — flipbook_viewer
  FLIPBOOK_VIEWER_VIEWED: 'flipbook_viewer_viewed',
  FLIPBOOK_VIEWER_PAGE_TURNED: 'flipbook_viewer_page_turned',
  FLIPBOOK_VIEWER_COMPLETED: 'flipbook_viewer_completed',
  FLIPBOOK_VIEWER_PRINT_CLICKED: 'flipbook_viewer_print_clicked',
  FLIPBOOK_VIEWER_EDIT_CLICKED: 'flipbook_viewer_edit_clicked',

  // B2C Funnel — edit_choice
  EDIT_CHOICE_VIEWED: 'edit_choice_viewed',
  EDIT_CHOICE_SELF_SELECTED: 'edit_choice_self_selected',
  EDIT_CHOICE_EXPERT_SELECTED: 'edit_choice_expert_selected',
  EDIT_CHOICE_EXPERT_DISABLED_SEEN: 'edit_choice_expert_disabled_seen',
  EDIT_CHOICE_BACK_TO_TRIPBOOK: 'edit_choice_back_to_tripbook',

  // B2C Funnel — expert_pages_modal
  EXPERT_PAGES_MODAL_VIEWED: 'expert_pages_modal_viewed',
  EXPERT_PAGES_OPTION_SELECTED: 'expert_pages_option_selected',
  EXPERT_PAGES_CONFIRMED: 'expert_pages_confirmed',
  EXPERT_PAGES_MODAL_CLOSED: 'expert_pages_modal_closed',

  // B2C Funnel — print_customise
  PRINT_CUSTOMISE_VIEWED: 'print_customise_viewed',
  PRINT_CUSTOMISE_TITLE_EDITED: 'print_customise_title_edited',
  PRINT_CUSTOMISE_COLOR_SELECTED: 'print_customise_color_selected',
  PRINT_CUSTOMISE_PAY_CLICKED: 'print_customise_pay_clicked',
  PRINT_CUSTOMISE_CLOSED: 'print_customise_closed',

  // B2C Funnel — checkout_details
  CHECKOUT_DETAILS_VIEWED: 'checkout_details_viewed',
  CHECKOUT_DETAILS_ADDRESS_ENTERED: 'checkout_details_address_entered',
  CHECKOUT_DETAILS_DISCOUNT_APPLIED: 'checkout_details_discount_applied',
  CHECKOUT_DETAILS_DISCOUNT_FAILED: 'checkout_details_discount_failed',
  CHECKOUT_DETAILS_PROCEED_CLICKED: 'checkout_details_proceed_clicked',
  CHECKOUT_DETAILS_BACK_CLICKED: 'checkout_details_back_clicked',

  // B2C Funnel — checkout_payment
  CHECKOUT_PAYMENT_VIEWED: 'checkout_payment_viewed',
  CHECKOUT_PAYMENT_METHOD_SELECTED: 'checkout_payment_method_selected',
  CHECKOUT_PAYMENT_LINK_CLICKED: 'checkout_payment_link_clicked',
  CHECKOUT_PAYMENT_COMPLETED: 'checkout_payment_completed',
  CHECKOUT_PAYMENT_FAILED: 'checkout_payment_failed',

  // B2C Funnel — editor_web
  //
  // Every event below is emitted through `trackEditorEvent`, so they all carry
  // the shared editor context (surface, trip/tripbook ids, page count…) — see
  // `EditorAnalyticsContext`. `surface` is what separates desktop from mobile.
  EDITOR_VIEWED: 'editor_viewed',
  EDITOR_EXPERT_BANNER_CLICKED: 'editor_expert_banner_clicked',
  EDITOR_PRINT_CLICKED: 'editor_print_clicked',
  EDITOR_COVER_EDITED: 'editor_cover_edited',

  // --- editor: lifecycle & navigation ---
  EDITOR_CLOSED: 'editor_closed',
  EDITOR_PREVIEW_OPENED: 'editor_preview_opened',
  EDITOR_PREVIEW_CLOSED: 'editor_preview_closed',
  EDITOR_UNDO_CLICKED: 'editor_undo_clicked',
  EDITOR_REDO_CLICKED: 'editor_redo_clicked',
  EDITOR_PAGE_OPENED: 'editor_page_opened',
  EDITOR_PAGE_NAVIGATED: 'editor_page_navigated',
  EDITOR_PAGE_CLOSED: 'editor_page_closed',
  EDITOR_VIEW_COVER_CLICKED: 'editor_view_cover_clicked',
  EDITOR_HELP_PANEL_OPENED: 'editor_help_panel_opened',
  EDITOR_OPTIONS_MENU_OPENED: 'editor_options_menu_opened',
  EDITOR_PRINT_BLOCKED_PAGE_COUNT: 'editor_print_blocked_page_count',
  EDITOR_PAGE_LIMIT_MODAL_VIEWED: 'editor_page_limit_modal_viewed',

  // --- editor: day texts ---
  EDITOR_DAY_TITLE_SAVED: 'editor_day_title_saved',
  EDITOR_DAY_DESCRIPTION_SAVED: 'editor_day_description_saved',
  EDITOR_DAY_BEST_MOMENT_SAVED: 'editor_day_best_moment_saved',
  EDITOR_PERSONALIZATION_FIELD_SAVED: 'editor_personalization_field_saved',
  EDITOR_AI_TEXTS_GENERATE_CLICKED: 'editor_ai_texts_generate_clicked',
  EDITOR_AI_TEXTS_DELETE_CLICKED: 'editor_ai_texts_delete_clicked',
  EDITOR_AI_TEXTS_COMPLETED: 'editor_ai_texts_completed',
  EDITOR_AI_TEXTS_FAILED: 'editor_ai_texts_failed',

  // --- editor: itinerary summary ---
  EDITOR_ITINERARY_MODAL_OPENED: 'editor_itinerary_modal_opened',
  EDITOR_ITINERARY_MODAL_CLOSED: 'editor_itinerary_modal_closed',
  EDITOR_ITINERARY_NODE_ADDED: 'editor_itinerary_node_added',
  EDITOR_ITINERARY_NODE_REMOVED: 'editor_itinerary_node_removed',
  EDITOR_ITINERARY_PHOTO_PICKED: 'editor_itinerary_photo_picked',
  EDITOR_ITINERARY_SAVED: 'editor_itinerary_saved',

  // --- editor: cover ---
  EDITOR_COVER_EDITOR_OPENED: 'editor_cover_editor_opened',
  EDITOR_COVER_EDITOR_CLOSED: 'editor_cover_editor_closed',
  EDITOR_COVER_TAB_CHANGED: 'editor_cover_tab_changed',
  // Mobile-only: which section (title / texts / places / photos) of the day and
  // trip-summary editors people actually use.
  EDITOR_DAY_TAB_CHANGED: 'editor_day_tab_changed',
  // Title/subtitle edits are text inputs — tracking them per keystroke would be
  // noise, so they're reported as `changed_title` / `changed_subtitle` flags on
  // `editor_cover_edited` when the cover is saved.
  EDITOR_COVER_FONT_CHANGED: 'editor_cover_font_changed',
  EDITOR_COVER_TEXT_COLOR_CHANGED: 'editor_cover_text_color_changed',
  EDITOR_COVER_VERSION_CHANGED: 'editor_cover_version_changed',
  EDITOR_COVER_TITLE_POSITION_CHANGED: 'editor_cover_title_position_changed',
  EDITOR_COVER_PHOTO_UPLOADED: 'editor_cover_photo_uploaded',
  EDITOR_COVER_PHOTO_REPOSITIONED: 'editor_cover_photo_repositioned',

  // --- editor: spine ---
  EDITOR_SPINE_DECORATION_CHANGED: 'editor_spine_decoration_changed',
  EDITOR_SPINE_ICON_CHANGED: 'editor_spine_icon_changed',
  EDITOR_SPINE_TITLE_POSITION_CHANGED: 'editor_spine_title_position_changed',

  // --- editor: photos ---
  EDITOR_PHOTO_SELECTED: 'editor_photo_selected',
  EDITOR_PHOTO_REPLACE_CLICKED: 'editor_photo_replace_clicked',
  EDITOR_PHOTO_REPLACED: 'editor_photo_replaced',
  EDITOR_PHOTO_SWAP_STARTED: 'editor_photo_swap_started',
  EDITOR_PHOTO_SWAPPED: 'editor_photo_swapped',
  EDITOR_PHOTO_SWAP_BLOCKED: 'editor_photo_swap_blocked',
  EDITOR_PHOTO_MOVE_OPENED: 'editor_photo_move_opened',
  EDITOR_PHOTO_MOVED: 'editor_photo_moved',
  EDITOR_PHOTO_FIT_CHANGED: 'editor_photo_fit_changed',
  EDITOR_PHOTO_ROTATED: 'editor_photo_rotated',
  EDITOR_PHOTO_DELETE_CLICKED: 'editor_photo_delete_clicked',
  EDITOR_PHOTO_DELETED: 'editor_photo_deleted',
  EDITOR_PHOTO_DELETE_CANCELLED: 'editor_photo_delete_cancelled',
  EDITOR_PHOTO_SET_AS_COVER: 'editor_photo_set_as_cover',
  EDITOR_PHOTO_SET_AS_MEMBER_IMAGE: 'editor_photo_set_as_member_image',

  // --- editor: gallery & layout ---
  EDITOR_GALLERY_OPENED: 'editor_gallery_opened',
  EDITOR_GALLERY_CLOSED: 'editor_gallery_closed',
  EDITOR_GALLERY_SIDEBAR_RESIZED: 'editor_gallery_sidebar_resized',
  EDITOR_GALLERY_UPLOAD_TO_TRIP_CLICKED: 'editor_gallery_upload_to_trip_clicked',
  EDITOR_GALLERY_UPLOAD_TO_DAY_CLICKED: 'editor_gallery_upload_to_day_clicked',
  EDITOR_GALLERY_UPLOAD_CLOSED: 'editor_gallery_upload_closed',
  EDITOR_GALLERY_ORGANIZE_AUTO_CLICKED: 'editor_gallery_organize_auto_clicked',
  EDITOR_GALLERY_ORGANIZE_AUTO_COMPLETED: 'editor_gallery_organize_auto_completed',
  EDITOR_GALLERY_ORGANIZE_AUTO_FAILED: 'editor_gallery_organize_auto_failed',
  EDITOR_GALLERY_SELECT_DUPLICATES: 'editor_gallery_select_duplicates',
  EDITOR_GALLERY_SELECT_SIMILAR: 'editor_gallery_select_similar',
  EDITOR_GALLERY_SELECT_ALL_DAY: 'editor_gallery_select_all_day',
  EDITOR_GALLERY_PHOTOS_MOVED_TO_DAY: 'editor_gallery_photos_moved_to_day',
  EDITOR_GALLERY_PHOTOS_DELETED: 'editor_gallery_photos_deleted',
  EDITOR_GALLERY_DISCARDED_EXPANDED: 'editor_gallery_discarded_expanded',
  EDITOR_GALLERY_PAGE_CLICKED: 'editor_gallery_page_clicked',
  EDITOR_LAYOUT_PHOTO_DROPPED: 'editor_layout_photo_dropped',
  EDITOR_LAYOUT_PAGE_MOVED: 'editor_layout_page_moved',
  EDITOR_LAYOUT_SAVED: 'editor_layout_saved',

  // --- editor: trip book settings ---
  EDITOR_COLOR_CHANGED: 'editor_color_changed',
  EDITOR_LANGUAGE_CHANGED: 'editor_language_changed',
  EDITOR_COUNTRIES_ADDED: 'editor_countries_added',
  EDITOR_COUNTRIES_REMOVED: 'editor_countries_removed',
  EDITOR_TRAVELLING_FROM_CHANGED: 'editor_travelling_from_changed',
  EDITOR_DATES_CHANGE_REQUESTED: 'editor_dates_change_requested',
  EDITOR_DATES_CONFIRMED: 'editor_dates_confirmed',
  EDITOR_DATES_CANCELLED: 'editor_dates_cancelled',
  EDITOR_AGENCY_CODE_SUBMITTED: 'editor_agency_code_submitted',
  EDITOR_AGENCY_CODE_ACCEPTED: 'editor_agency_code_accepted',
  EDITOR_AGENCY_CODE_REJECTED: 'editor_agency_code_rejected',
  EDITOR_AGENCY_CODE_REMOVED: 'editor_agency_code_removed',
  EDITOR_TRIPBOOK_DUPLICATED: 'editor_tripbook_duplicated',
  EDITOR_TRIPBOOK_DELETE_CLICKED: 'editor_tripbook_delete_clicked',
  EDITOR_TRIPBOOK_DELETED: 'editor_tripbook_deleted',
  EDITOR_TRIPBOOK_ROLLBACK_OPENED: 'editor_tripbook_rollback_opened',
  EDITOR_TRIPBOOK_ROLLBACK_CONFIRMED: 'editor_tripbook_rollback_confirmed',
  EDITOR_TRIPBOOK_ROLLBACK_FAILED: 'editor_tripbook_rollback_failed',
  EDITOR_EXPERIENCES_ORGANIZER_OPENED: 'editor_experiences_organizer_opened',
  EDITOR_EXPERIENCES_REORDERED: 'editor_experiences_reordered',
  EDITOR_EXPERIENCE_CREATED: 'editor_experience_created',
  EDITOR_EXPERIENCE_DELETED: 'editor_experience_deleted',

  // --- editor: travelers ---
  EDITOR_TRAVELER_SAVED: 'editor_traveler_saved',
  EDITOR_TRAVELER_DELETED: 'editor_traveler_deleted',

  // --- editor: featured places ---
  EDITOR_PLACE_ADD_OPENED: 'editor_place_add_opened',
  EDITOR_PLACE_SEARCHED: 'editor_place_searched',
  EDITOR_PLACE_SEARCH_FAILED: 'editor_place_search_failed',
  EDITOR_PLACE_ADDED: 'editor_place_added',
  EDITOR_PLACE_EDITED: 'editor_place_edited',
  EDITOR_PLACE_DELETED: 'editor_place_deleted',
  EDITOR_PLACE_REORDERED: 'editor_place_reordered',

  // --- editor: errors & friction ---
  EDITOR_SAVE_FAILED: 'editor_save_failed',
  EDITOR_ACCESS_DENIED: 'editor_access_denied',
  EDITOR_TRIPBOOK_NOT_FOUND: 'editor_tripbook_not_found',
  EDITOR_BROKEN_IMAGES_RESTORE_CLICKED: 'editor_broken_images_restore_clicked',
  EDITOR_BROKEN_IMAGES_RESTORE_FAILED: 'editor_broken_images_restore_failed',

  // Global
  FUNNEL_STEP_ABANDONED: 'funnel_step_abandoned',

  // Auth — login / signup outcomes
  USER_LOGIN_FAILED: 'user_login_failed',
  USER_SIGNUP_FAILED: 'user_signup_failed',

  // Auth — recovering password sign-in after a social link dropped it
  AUTH_ADD_PASSWORD_PROMPTED: 'auth_add_password_prompted',
  AUTH_ADD_PASSWORD_ADDED: 'auth_add_password_added',
  AUTH_ADD_PASSWORD_SKIPPED: 'auth_add_password_skipped',
  AUTH_ADD_PASSWORD_FAILED: 'auth_add_password_failed'
}
